import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { get, isEqual, set } from 'lodash/fp';
import { ComponentProps, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { validateNonEmptyRule } from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/utils';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { TOOLBAR_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { FLE_TOOLBAR_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar/InquiryTemplateEditorToolbar';
import EditOrRulesBlock from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/EditOrRulesBlock';
import { LOGIC_RULE_INPUT_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/FieldOrValueInput';
import { DEFAULT_LOGIC_RULE_BLOCK } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/constants';
import {
  ComponentJsonLogicKey,
  LogicRuleSection,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import {
  getDefaultStateLogicKey,
  getNormalizedLogicKey,
  getOperatorsMap,
  isInvertedLogicKey,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import Divider from 'dashboard/components/Divider';
import FormError from 'dashboard/components/FormControls/FormError';
import ReactSelect from 'dashboard/components/ReactSelect';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import PositionedPanel from 'dashboard/components-lib/MaximizablePanel/PositionedPanel';
import { FULL_SCREEN_HEADER_HEIGHT_XS } from 'dashboard/layouts/ShowViewLayoutV2';
import { ConditionalRule, LogicBlockOrV2, LogicBlockV2 } from 'dashboard/lib/workflows';
import { INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID } from 'dashboard/views/DashboardInquiryTemplatesShowView/DashboardInquiryTemplatesShowView';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema } from 'lib/json-schema';

const PREFIX_RULES_TEXT = 'When';

type ComponentConditional = {
  name?: string;
  rules: LogicBlockOrV2;
};

const getNormalizedRules = (rules: LogicBlockOrV2, isInvertedRule: boolean) => {
  return isInvertedRule ? { '!': rules } : rules;
};

/**
 * Recursively checks after deleting the rule if the parent rule is empty
 * and returns the updated rule and path to update
 *
 * Sample initial paths:
 * non-nested: ['rules', 'or', '0', 'and']
 * nested:     ['rules', 'or', '0', 'and', '1', 'or', '2', 'and']
 */
const getDeleteUpdatedRuleAndPath = (
  rule: ComponentConditional | LogicBlockV2[],
  path: string[],
  index: number,
): { updatedRule: LogicBlockV2[]; pathToUpdate: string[] } => {
  const updatedRule = (get(path, rule) as LogicBlockV2[]).filter((_, i) => i !== index);
  if (updatedRule.length === 0) {
    if (isEqual(['rules', 'or'], path)) {
      // terminal case is all rules have been deleted - reset to default empty block
      return { updatedRule: DEFAULT_LOGIC_RULE_BLOCK.rules.or, pathToUpdate: path };
    } else {
      return getDeleteUpdatedRuleAndPath(
        rule,
        path.slice(0, path.length - 2),
        parseInt(path[path.length - 2]),
      );
    }
  } else {
    return { updatedRule, pathToUpdate: path };
  }
};

const StyledDivider = styled(Divider)`
  margin-top: 0;
  margin-bottom: ${Spacing[3]};
`;

interface ComponentConditionalLogicPanelProps {
  conditional: ComponentConditional;
  logicRuleSection: LogicRuleSection;
  selectedLogicKey: ComponentJsonLogicKey | null;
  logicKeyOptions: Array<{
    key: string;
    label: string;
    disabled: boolean;
  }>;
  jsonSchema: JsonSchema;
  title?: string;
  description?: string;
  outsideExceptionalIdPrefixes?: ComponentProps<
    typeof PositionedPanel
  >['outsideExceptionalIdPrefixes'];
  isInvertedRule: boolean;
  editable: boolean;
  onOutsideClick: () => void;
  updateConditional: (
    conditional: LogicBlockOrV2 | { '!': LogicBlockOrV2 } | null,
    logicKey: ComponentJsonLogicKey | null,
  ) => void;
}

const ComponentConditionalLogicPanel = ({
  conditional,
  logicRuleSection,
  selectedLogicKey,
  logicKeyOptions,
  jsonSchema,
  title,
  description,
  outsideExceptionalIdPrefixes,
  isInvertedRule,
  editable,
  onOutsideClick,
  updateConditional,
}: ComponentConditionalLogicPanelProps) => {
  const isITEInternal = useIsITEInternal();
  const enableScreenEditorLogicCodeEditor = useFeatureFlag(
    'dashboard:ite-enable-screen-editor-logic-code-editor',
  );
  const [updatedConditional, setUpdatedConditional] = useState(conditional);
  const [logicCodeEditorOpen, { toggle: toggleJSONLogicCodeEditor }] = useToggle(false);
  const [isWrittenRuleInvalidJson, setIsWrittenRuleInvalidJson] = useState(false);

  useEffect(() => {
    setUpdatedConditional(conditional);
  }, [conditional]);

  const updateConditionalRule = useCallback(
    (conditional: ComponentConditional) => {
      setUpdatedConditional(conditional);
      updateConditional(getNormalizedRules(conditional.rules, isInvertedRule), selectedLogicKey);
    },
    [isInvertedRule, selectedLogicKey, updateConditional],
  );

  const updateRule = useCallback(
    (path: string[]) => (rule: ConditionalRule) => {
      const newConditional = set(['rules', 'or', ...path], rule, updatedConditional);
      updateConditionalRule(newConditional);
    },
    [updatedConditional, updateConditionalRule],
  );

  const deleteRule = useCallback(
    (path: string[], index: number) => {
      const { updatedRule, pathToUpdate } = getDeleteUpdatedRuleAndPath(
        updatedConditional,
        ['rules', 'or', ...path],
        index,
      );
      const newConditional = set(pathToUpdate, updatedRule, updatedConditional);
      updateConditionalRule(newConditional);
    },
    [updatedConditional, updateConditionalRule],
  );

  const addAndRule = useCallback(
    (path: string[], nestedGroup: boolean = false) => {
      const newRule = nestedGroup ? { or: [{ and: [{}] }] } : {};
      const andRules = get(['rules', 'or', ...path], updatedConditional);
      const newConditional = set(
        ['rules', 'or', ...path],
        [...andRules, newRule],
        updatedConditional,
      );
      updateConditionalRule(newConditional);
    },
    [updatedConditional, updateConditionalRule],
  );

  const addOrRule = useCallback(
    (path: string[] = ['or']) => {
      const orRules = get(['rules', ...path], updatedConditional);
      const newConditional = set(
        ['rules', ...path],
        orRules.concat({ and: [{}] }),
        updatedConditional,
      );
      updateConditionalRule(newConditional);
    },
    [updatedConditional, updateConditionalRule],
  );

  const updateLogicKey = useCallback(
    (selectedAttribute: ComponentJsonLogicKey) => {
      updateConditional(
        getNormalizedRules(updatedConditional.rules, isInvertedLogicKey(selectedAttribute)),
        getDefaultStateLogicKey(selectedAttribute),
      );
    },
    [updatedConditional, updateConditional],
  );

  const deleteLogicRule = useCallback(
    () => updateConditional(null, selectedLogicKey),
    [selectedLogicKey, updateConditional],
  );

  const operatorsMap = getOperatorsMap(logicRuleSection);

  return createPortal(
    <MaximizablePanel
      title={<Heading size='sm' as='h2'>{`Logic Rule${title ? `: ${title}` : ''}`}</Heading>}
      subtitle={description}
      offsetElementId={SCREEN_EDITOR_CONTROL_PANEL_ID}
      fullscreenTop={TOOLBAR_HEIGHT + FULL_SCREEN_HEADER_HEIGHT_XS}
      outsideExceptionalIdPrefixes={[
        ...(outsideExceptionalIdPrefixes || []),
        FLE_TOOLBAR_ID,
        LOGIC_RULE_INPUT_ID,
      ]}
      rightButton={
        <HStack equal>
          {(isITEInternal || enableScreenEditorLogicCodeEditor) && selectedLogicKey != null && (
            <ToggleSlider
              titleOn='Code editor open'
              titleOff='Code editor closed'
              labelOn='Close code editor'
              labelOff='Open code editor'
              value={logicCodeEditorOpen}
              onChange={() => {
                toggleJSONLogicCodeEditor();
                // clear the error when toggling on/off since the invalid rule doesn't get persisted
                setIsWrittenRuleInvalidJson(false);
              }}
            />
          )}
          <Button
            width='icon'
            variant='inverse'
            aria-label='Delete logic rule'
            id='component-conditional-logic-panel__delete-button'
            disabled={!editable || selectedLogicKey == null}
            onClick={deleteLogicRule}
          >
            <TrashIcon />
          </Button>
        </HStack>
      }
      onOutsideClickAndEscPress={onOutsideClick}
      containerStyleOverride={css`
        min-height: max-content;
      `}
    >
      <StyledDivider full />
      {logicCodeEditorOpen && selectedLogicKey != null ? (
        <>
          <CodeEditor
            language='json'
            code={JSON.stringify(updatedConditional.rules, null, 2)}
            completionSources={{}}
            readOnly={!editable}
            onChange={(updatedConditional: string) => {
              // check if the updated conditional is a valid JSON
              let parsedUpdatedConditional = null;
              try {
                parsedUpdatedConditional = JSON.parse(updatedConditional);
              } catch (e) {
                setIsWrittenRuleInvalidJson(true);
                return;
              }
              setIsWrittenRuleInvalidJson(false);
              updateConditional(
                getNormalizedRules(parsedUpdatedConditional, isInvertedRule),
                selectedLogicKey,
              );
            }}
          />
          {isWrittenRuleInvalidJson && <FormError>Rule is not a valid JSON</FormError>}
        </>
      ) : (
        <>
          <div style={{ width: '400px' }}>
            <Label size='md' weight='bold'>
              Apply component update:
            </Label>
            <Spacer height={1} />
            <ReactSelect
              id={`${LOGIC_RULE_INPUT_ID}__select`}
              aria-label='Logic key select'
              value={
                selectedLogicKey != null
                  ? getNormalizedLogicKey(selectedLogicKey, isInvertedRule)
                  : ''
              }
              options={logicKeyOptions.map(({ key, label, disabled }) => ({
                value: key,
                label,
                disabled,
              }))}
              disabled={!editable || logicKeyOptions.length === 1}
              hasError={selectedLogicKey == null}
              onChange={updateLogicKey}
              isSelectedOptionRequired
              portal
            />
            {selectedLogicKey == null && <FormError>Select component update</FormError>}
          </div>
          <Spacer height={2} />
          <EditOrRulesBlock
            keyPrefix='component-conditional__'
            rules={updatedConditional.rules}
            jsonSchema={jsonSchema}
            prefixText={PREFIX_RULES_TEXT}
            operatorsMap={operatorsMap}
            editable={editable && selectedLogicKey != null}
            enableValidation={selectedLogicKey != null}
            validateRule={validateNonEmptyRule}
            updateRule={updateRule}
            deleteRule={deleteRule}
            addAndRule={addAndRule}
            addOrRule={addOrRule}
          />
        </>
      )}
    </MaximizablePanel>,
    document.getElementById(INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID) as Element,
  );
};

export default ComponentConditionalLogicPanel;
